import {jsx} from 'panel';

import {InputEl} from 'common/components/input';
import {parseNumber} from 'common/util';
import {NumberOperator} from 'common/widgets/property-filter-menu/models/types';
import {InputChangeEvent, InputSize, InputTheme} from 'common/components/input/types';

import {FilterValueProps} from '../types';

const MULTIPLE_VALUES_OPERATORS = new Set<NumberOperator>([NumberOperator.Between, NumberOperator.NotBetween]);

export function FilterNumericValue({filter, isActive, onChange, readonly, hasNewUI}: FilterValueProps) {
  // set and not set do not require a filter value input
  if (
    (filter.filterOperator as NumberOperator) === NumberOperator.Set ||
    (filter.filterOperator as NumberOperator) === NumberOperator.NotSet
  ) {
    return null;
  }

  const {filterOperator, filterValue} = filter;

  const inputEl = (
    currentValue: number,
    handleInput: (number) => void,
    secondIndex?: number,
    centerInput?: boolean,
  ) => {
    const isInvalid = parseNumber(currentValue) === null;
    const shouldAutoFocus = isActive && !secondIndex;
    const value = currentValue?.toString(10) ?? ``;

    return (
      <InputEl
        class={{'is-placeholder': isInvalid, 'center-input': centerInput}}
        attrs={{
          elref: `filterInput`,
          size: hasNewUI ? InputSize.Small : InputSize.Medium,
          type: `number`,
          placeholder: `Value…`,
          'auto-focus': !!shouldAutoFocus,
          'select-on-focus': true,
          readonly,
          theme: hasNewUI ? InputTheme.New : InputTheme.Default,
        }}
        props={{value}}
        on={{
          change: (ev: InputChangeEvent) => {
            const input = ev.target as HTMLInputElement;

            // when the updated value is not a valid input, revert back to previously committed input
            const updatedValue = parseNumber(ev.detail.value);
            if (updatedValue !== null) {
              handleInput(updatedValue);
            } else {
              input.value = value;
            }

            input.blur(); // user has most likely pressed "Enter", in which case they are likely finished so we want to lose focus
          },
        }}
      />
    );
  };

  // handle cases where the numeric filter requires 2 values
  if (MULTIPLE_VALUES_OPERATORS.has(filterOperator as NumberOperator)) {
    const [minValue, maxValue] = filterValue as number[];
    return (
      <>
        {inputEl(minValue, (value) => onChange([value, maxValue]), 0, hasNewUI)}
        <div sel='.entry-filter-conjunction'>and</div>
        {inputEl(maxValue, (value) => onChange([minValue, value]), 1, hasNewUI)}
      </>
    );
  }

  return <>{inputEl(filterValue as number, (value) => onChange(value))}</>;
}
